import { useEffect, useRef } from 'react';

interface TurnstileWidgetProps {
  siteKey: string;
  onToken: (token: string) => void;
  resetKey: number;
}

export function TurnstileWidget({
  siteKey,
  onToken,
  resetKey,
}: TurnstileWidgetProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!siteKey) {
      return;
    }

    let cancelled = false;
    let interval: number | undefined;

    const renderWidget = () => {
      if (cancelled || !containerRef.current || !window.turnstile) {
        return;
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        appearance: 'execute',
        callback: (token: string) => {
          onToken(token);
        },
        'error-callback': () => {
          onToken('');
        },
        'expired-callback': () => {
          onToken('');
        },
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      interval = window.setInterval(() => {
        if (cancelled) {
          if (typeof interval !== 'undefined') {
            window.clearInterval(interval);
          }
          return;
        }
        if (window.turnstile && containerRef.current) {
          window.clearInterval(interval);
          interval = undefined;
          renderWidget();
        }
      }, 250);
    }

    return () => {
      cancelled = true;
      if (typeof interval !== 'undefined') {
        window.clearInterval(interval);
      }
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
      onToken('');
    };
  }, [siteKey, onToken, resetKey]);

  return <div ref={containerRef} className="turnstile-widget" />;
}
